import { isEqual } from 'lodash'
import moment from 'moment'
import { useMemo, useState, type ReactElement } from 'react'
import { Calendar, type Event, momentLocalizer, Views } from 'react-big-calendar'
import { CaretLeftIcon, CaretRightIcon, PlusIcon, XIcon } from '@phosphor-icons/react'
import { TimeInput } from 'app/components/form/OfficeHoursEditor'
import { defaultOpenHours, newSlotFromSeed, type DayKey } from 'app/lib/officeHours'
import { SectionCard } from 'app/components/SectionCard'
import { Button } from 'app/components/ui/button'
import { Label } from 'app/components/ui/label'
import { Switch } from 'app/components/ui/switch'
import { tinyClock } from 'app/lib/aiAgentAvailability'
import { toOpenHoursInput, type HoursLine } from 'app/lib/aiAgentOverview'
import { cn } from 'app/lib/utils'
import { useAIAgentStore, useLocationStore } from 'app/stores/StoreContext'
import type { OpenHours } from 'app/types/entities'
import { useUser } from 'app/utils'
import { useScheduleHoursPreview } from './useScheduleHoursPreview'
import { useSettingsSection } from './useSettingsSave'

/** With this compiler tool on, the agent reads business hours from the practice schedule. */
const HOURS_FROM_SCHEDULE_TOOL = `business_hours_from_schedule`

function SourceChip({ fromSchedule }: { fromSchedule: boolean }): ReactElement {
  return (
    <span className="rounded-full border border-default px-2 py-0.5 text-body-xs text-default">
      {fromSchedule ? `PMS Business Hours` : `Manual Business Hours`}
    </span>
  )
}

type HoursEvent = Event & { resource: { open: boolean } }

const localizer = momentLocalizer(moment)
const FORMATS = { dateFormat: `D` }

/** The next 30 days on the shared month calendar, each day with the hours the schedule implies. */
function SchedulePreview({ locationId, timezone }: { locationId: string; timezone?: string | null }): ReactElement {
  const { loading, failed, days } = useScheduleHoursPreview(locationId, true, timezone)
  const [monthOffset, setMonthOffset] = useState(0)
  const events = useMemo<HoursEvent[]>(
    () =>
      days.map((day) => {
        const date = moment(day.date).toDate()
        const open = !!(day.from && day.to)
        const title = open ? `${tinyClock(day.from!)}–${tinyClock(day.to!)}` : `Closed`
        return { title, start: date, end: date, allDay: true, resource: { open } }
      }),
    [days],
  )
  if (loading) return <div className="h-48 animate-pulse rounded-lg bg-surface-muted" />
  if (failed || days.length === 0) {
    return <p className="text-body-sm text-subtle">The practice schedule could not be loaded for a preview.</p>
  }
  const first = moment(days[0].date)
  const last = moment(days[days.length - 1].date)
  const lastOffset = last.diff(first.clone().startOf(`month`), `months`)
  const month = first.clone().add(monthOffset, `months`)
  const inWindow = (date: Date) => moment(date).isBetween(first, last, `day`, `[]`)
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <p className="text-body-xs text-subtle">Next 30 days, from your providers&apos; schedules.</p>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="tertiary"
            size="icon-sm"
            aria-label="Previous month"
            disabled={monthOffset === 0}
            onClick={() => setMonthOffset(monthOffset - 1)}
          >
            <CaretLeftIcon className="size-4" />
          </Button>
          <span className="min-w-24 text-center text-body-sm text-strong">{month.format(`MMMM YYYY`)}</span>
          <Button
            type="button"
            variant="tertiary"
            size="icon-sm"
            aria-label="Next month"
            disabled={monthOffset >= lastOffset}
            onClick={() => setMonthOffset(monthOffset + 1)}
          >
            <CaretRightIcon className="size-4" />
          </Button>
        </div>
      </div>
      <div className="h-104">
        <Calendar
          localizer={localizer}
          events={events}
          date={month.toDate()}
          getNow={() => first.toDate()}
          view={Views.MONTH}
          views={[Views.MONTH]}
          toolbar={false}
          formats={FORMATS}
          // The buttons above own navigation; no day opens or selects.
          onNavigate={() => undefined}
          onView={() => undefined}
          drilldownView={null}
          dayPropGetter={(date) => ({ className: inWindow(date) ? undefined : `bg-surface-subtle` })}
          components={{
            month: {
              dateHeader: ({ date, label }) => (
                <span className={inWindow(date) ? `text-strong` : `text-minimal`}>{label}</span>
              ),
            },
            event: ({ event }: { event: HoursEvent }) => (
              <span
                className={cn(
                  `block truncate text-body-xs tabular-nums`,
                  event.resource.open ? `text-strong` : `text-minimal`,
                )}
              >
                {event.title}
              </span>
            ),
          }}
          eventPropGetter={(event) => ({
            className: event.resource.open ? `bg-surface-muted` : `bg-transparent`,
          })}
        />
      </div>
    </div>
  )
}

function WeeklyHours({ hours }: { hours: HoursLine[] }): ReactElement {
  const openDays = hours.filter((line) => line.hours !== `Closed`)
  return (
    <div className="flex flex-col gap-2 text-body-sm text-strong">
      <span className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
        {hours.map((line) => (
          <span key={line.day} className="flex gap-3">
            <span className="w-9 shrink-0 text-subtle">{line.day}</span>
            <span className={line.hours === `Closed` ? `text-subtle` : undefined}>{line.hours}</span>
          </span>
        ))}
      </span>
      {openDays.length === 0 && (
        <span className="text-subtle">
          No hours are set on this location, so this is what callers hear: closed every day.
        </span>
      )}
    </div>
  )
}

const DAYS: DayKey[] = [`sunday`, `monday`, `tuesday`, `wednesday`, `thursday`, `friday`, `saturday`]
const TIME_INPUT = `h-9 min-w-0 flex-1 rounded-lg border border-default bg-surface px-2.5 text-body-sm text-strong tabular-nums max-md:[&::-webkit-calendar-picker-indicator]:hidden md:w-32 md:flex-none`

/** The week as one tight column: a switch per day, its hours beside it, "Closed" when it is off. */
function WeeklyHoursEditor({ value, onChange }: { value: OpenHours; onChange: (next: OpenHours) => void }) {
  const setDay = (day: DayKey, patch: Partial<OpenHours[DayKey]>) =>
    onChange({ ...value, [day]: { ...value[day], ...patch } })

  return (
    <div className="flex flex-col">
      {DAYS.map((day) => {
        const open = value[day]?.open ?? false
        const slots = value[day]?.hours ?? []
        return (
          <div
            key={day}
            className="flex flex-col gap-2 border-b border-subtle py-2 last:border-b-0 md:flex-row md:items-start md:gap-4"
          >
            <div className="flex h-9 shrink-0 items-center gap-3 md:w-36">
              <Switch
                aria-label={`Open on ${day}`}
                checked={open}
                onCheckedChange={(checked) =>
                  setDay(day, {
                    open: checked,
                    hours: checked && slots.length === 0 ? [newSlotFromSeed(undefined, day)] : slots,
                  })
                }
              />
              <span className={cn(`text-body-sm capitalize`, open ? `text-strong` : `text-subtle`)}>{day}</span>
            </div>
            {open ? (
              <div className="flex min-w-0 flex-col gap-2">
                {slots.map((slot, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <TimeInput
                      className={TIME_INPUT}
                      value={slot.from}
                      onCommit={(from) =>
                        setDay(day, { hours: slots.map((s, i) => (i === index ? { ...s, from } : s)) })
                      }
                    />
                    <span className="text-body-sm text-subtle">to</span>
                    <TimeInput
                      className={TIME_INPUT}
                      value={slot.to}
                      onCommit={(to) => setDay(day, { hours: slots.map((s, i) => (i === index ? { ...s, to } : s)) })}
                    />
                    {index === 0 ? (
                      <Button
                        type="button"
                        variant="tertiary"
                        size="icon-sm"
                        aria-label={`Add hours on ${day}`}
                        onClick={() => setDay(day, { hours: [...slots, newSlotFromSeed(value, day)] })}
                      >
                        <PlusIcon className="size-4" />
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="tertiary"
                        size="icon-sm"
                        aria-label={`Remove these hours on ${day}`}
                        onClick={() => setDay(day, { hours: slots.filter((_, i) => i !== index) })}
                      >
                        <XIcon className="size-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <span className="flex h-9 items-center text-body-sm text-subtle">Closed</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Business Hours on Settings. Everyone sees where the hours come from: set by hand on the location,
 * or pulled from the PMS schedule with a 30-day preview. Super users switch the source and edit the
 * hand-set hours in place; the page's save bar saves them.
 */
export function AiAgentBusinessHours({
  locationId,
  hours,
  openHours,
  fromSchedule,
  voiceAgentTools,
  timezone,
}: {
  locationId: string
  hours: HoursLine[]
  openHours: OpenHours | null | undefined
  fromSchedule: boolean
  voiceAgentTools: string[]
  timezone?: string | null
}): ReactElement {
  const isSuper = useUser()?.type === `super`
  const locationStore = useLocationStore()
  const agentStore = useAIAgentStore()
  const [draft, setDraft] = useState<{ fromSchedule: boolean; openHours: OpenHours } | null>(null)

  const shown = draft ?? { fromSchedule, openHours: openHours ?? defaultOpenHours }
  const sourceChanged = shown.fromSchedule !== fromSchedule
  const hoursChanged = !shown.fromSchedule && !isEqual(toOpenHoursInput(shown.openHours), toOpenHoursInput(openHours))

  useSettingsSection(`hours`, {
    dirty: !!draft && (sourceChanged || hoursChanged),
    discard: () => setDraft(null),
    save: async () => {
      if (sourceChanged) {
        const others = voiceAgentTools.filter((tool) => tool !== HOURS_FROM_SCHEDULE_TOOL)
        await agentStore.updateVoiceAgentTools(
          locationId,
          shown.fromSchedule ? [...others, HOURS_FROM_SCHEDULE_TOOL] : others,
        )
      }
      if (hoursChanged) {
        const result = await locationStore.update(locationId, { openHours: toOpenHoursInput(shown.openHours) })
        if (result.error) throw result.error
      }
    },
  })

  if (isSuper) {
    return (
      <SectionCard attached title="Business Hours" headerButtons={<SourceChip fromSchedule={shown.fromSchedule} />}>
        <div className="flex flex-col gap-5">
          <div className="flex items-center justify-between gap-6">
            <div className="flex flex-col gap-0.5">
              <Label htmlFor="hours-from-schedule">Pull business hours from the PMS schedule</Label>
              <span className="text-body-xs text-subtle">
                On: the agent reads each day&apos;s hours from the schedule the practice software sends. Off: it uses
                the hours set here.
              </span>
            </div>
            <Switch
              id="hours-from-schedule"
              checked={shown.fromSchedule}
              onCheckedChange={(checked) => setDraft({ ...shown, fromSchedule: checked })}
            />
          </div>
          {shown.fromSchedule ? (
            <SchedulePreview locationId={locationId} timezone={timezone} />
          ) : (
            <WeeklyHoursEditor value={shown.openHours} onChange={(next) => setDraft({ ...shown, openHours: next })} />
          )}
        </div>
      </SectionCard>
    )
  }

  return (
    <SectionCard attached title="Business Hours" headerButtons={<SourceChip fromSchedule={fromSchedule} />}>
      {fromSchedule ? (
        <div className="flex flex-col gap-4">
          <p className="text-body-sm text-strong">
            We automatically pull your business hours from your practice software&apos;s schedule, so the agent follows
            whatever it has for each day.
          </p>
          <SchedulePreview locationId={locationId} />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-body-sm text-subtle">These hours are set by hand. Contact support to change them.</p>
          <WeeklyHours hours={hours} />
        </div>
      )}
    </SectionCard>
  )
}
